import type { ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";

interface ModalShellProps {
  children: ReactNode;
  description?: string;
  footer?: ReactNode;
  onClose: () => void;
  open: boolean;
  title: string;
  widthClassName?: string;
}

export function ModalShell({
  children,
  description,
  footer,
  onClose,
  open,
  title,
  widthClassName = "max-w-2xl"
}: ModalShellProps) {
  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-[80] flex items-center justify-center bg-navy/40 px-4 py-8 backdrop-blur-sm"
          exit={{ opacity: 0 }}
          initial={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className={`surface w-full ${widthClassName} overflow-hidden`}
            exit={{ opacity: 0, scale: 0.98, y: 12 }}
            initial={{ opacity: 0, scale: 0.98, y: 20 }}
            onClick={(event) => event.stopPropagation()}
            transition={{ duration: 0.22, ease: "easeOut" }}
          >
            <div className="border-b border-line/70 px-6 py-5 md:px-8">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="eyebrow">Portal Review</p>
                  <h2 className="mt-2 text-2xl font-semibold tracking-tight text-ink">{title}</h2>
                  {description ? (
                    <p className="mt-2 max-w-xl text-sm leading-6 text-slate">{description}</p>
                  ) : null}
                </div>

                <button className="btn-ghost px-3 py-2" onClick={onClose} type="button">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="px-6 py-6 md:px-8">{children}</div>
            {footer ? <div className="border-t border-line/70 px-6 py-5 md:px-8">{footer}</div> : null}
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
